import React, { useState, useCallback, useEffect, useRef } from "react";
import { FaRegSquarePlus } from "react-icons/fa6";
import { AiOutlineCloseCircle } from "react-icons/ai";
import LoadingImage from "./assets/Loading.gif";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import HTTP_BASE_URL from "../constants/HttpConstants";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const toastrOptions = {
  position: "top-center",
  autoClose: 1000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "colored",
};

const validateCoordinates = (lat, lng) => {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !isNaN(lat) &&
    !isNaN(lng)
  );
};

const defaultCenter = { lat: 13.060644638899205, lng: 80.27414512422938 };

const getLatLngFromString = (latLngStr) => {
  if (!latLngStr) return defaultCenter;
  const [lat, lng] = latLngStr.split(",").map(parseFloat);
  return validateCoordinates(lat, lng) ? { lat, lng } : defaultCenter;
};

const BookingLocations = ({ close, id }) => {
  const customertoken = localStorage.getItem("customertoken");
  const routeParam = id;
  //map new
  // const place = autocompleteRef.current.getPlace();
  //const autocompleteRef = useRef(null); // 👈 for search box

  const handlePlaceSelect = (type, ref) => {
    const place = ref.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const address = place.formatted_address;

    setLocationValues((prev) => ({
      ...prev,
      [`${type}location`]: address,
      [`${type}location_latlon`]: `${lat},${lng}`,
    }));

    map.panTo({ lat, lng });
    setActiveMarker(type); // Show correct marker
  };

  const navigate = useNavigate();
  const pickUpTimeRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey:
      "AIzaSyAX84ZKpYIp9O7prq3tsjFn_G4eeEdb9KY&libraries=places&loading=async",
  });
  const [center, setCenter] = useState(defaultCenter);
  const [map, setMap] = useState(null);
  // const [markerPickup, setMarkerPickup] = useState({ lat: 0, lng: 0 });
  // const [markerDelivery, setMarkerDelivery] = useState({ lat: 0, lng: 0 });
  const [activeMarker, setActiveMarker] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [clear, setClear] = useState(false);
  const [locationValues, setLocationValues] = useState({
    pickuplocation: "",
    pickuplocation_latlon: "",
    deliverylocation: "",
    deliverylocation_latlon: "",
  });
  const pickupAutocompleteRef = useRef(null);
  const deliveryAutocompleteRef = useRef(null);
  const [errors, setErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [closePopup, setClosePopup] = useState(null);

  const getFullDetails = useCallback(() => {
    const myHeaders = new Headers();
    myHeaders.append("access-token", "508b1430-68f0-4b2f-9003-3c2a0eb2a54a");
    myHeaders.append("customertoken", customertoken);
    const formdata = new FormData();
    formdata.append("id", id);
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formdata,
      redirect: "follow",
    };

    fetch(HTTP_BASE_URL + "getBookingdetails", requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.code === 0) {
          setLocationValues(result.data.booking_details);
          // console.log("location", result.data);
        } else {
          navigate("/");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }, [id, customertoken, navigate]);

  const tokenExpiry = useCallback(() => {
    if (!customertoken) {
      // Step 1: Clear storage
      localStorage.clear(); // or sessionStorage.clear();
      // Step 2: Redirect to login or home page
      navigate("/", { replace: true });
      // Step 3: Prevent going back to the previous page
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, "", window.location.href);
      };
    }
  }, [customertoken, navigate]);

  useEffect(() => {
    getFullDetails();
    tokenExpiry();
  }, [getFullDetails, tokenExpiry]);

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setLocationValues((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const safeSetCenter = (lat, lng) => {
    if (validateCoordinates(lat, lng)) {
      setCenter({ lat, lng });
    } else {
      console.error("Invalid center coordinates:", { lat, lng });
    }
  };

  const goToCurrentPickupLocation = () => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      alert("Geolocation is not supported by this browser.");
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;

          setLocationValues((prevState) => {
            const updatedCenter = !prevState.pickuplocation_latlon
              ? { lat: currentLat, lng: currentLng }
              : getLatLngFromString(prevState.pickuplocation_latlon);

            safeSetCenter(updatedCenter.lat, updatedCenter.lng);

            return {
              ...prevState,
              pickuplocation_latlon: prevState.pickuplocation_latlon || "",
              // `${currentLat},${currentLng}`,   // current location select
            };
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const goToCurrentDeliveryLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;

          setLocationValues((prevState) => {
            const updatedCenter = !prevState.deliverylocation_latlon
              ? { lat: currentLat, lng: currentLng }
              : getLatLngFromString(prevState.deliverylocation_latlon);

            safeSetCenter(updatedCenter.lat, updatedCenter.lng);

            return {
              ...prevState,
              deliverylocation_latlon: prevState.deliverylocation_latlon || "",
              // `${currentLat},${currentLng}`,    // current location select
            };
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const onMapClick = useCallback(
    (event) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (!validateCoordinates(newPosition.lat, newPosition.lng)) {
        console.error("Invalid click position:", newPosition);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          if (activeMarker === "pickup") {
            setLocationValues((prevState) => ({
              ...prevState,
              pickuplocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              pickuplocation: formattedAddress,
            }));
          } else {
            setLocationValues((prevState) => ({
              ...prevState,
              deliverylocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              deliverylocation: formattedAddress,
            }));
          }
          if (map) {
            safeSetCenter(newPosition.lat, newPosition.lng);
          }
        } else {
          console.error("Geocoder failed or no results found.");
        }
      });
    },
    [activeMarker, map]
  );

  const onMarkerDragEnd = useCallback(
    (event, markerType) => {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      if (!validateCoordinates(newPosition.lat, newPosition.lng)) {
        console.error("Invalid drag position:", newPosition);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          if (markerType === "pickup") {
            setLocationValues((prevState) => ({
              ...prevState,
              pickuplocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              pickuplocation: formattedAddress,
            }));
          } else if (markerType === "delivery") {
            setLocationValues((prevState) => ({
              ...prevState,
              deliverylocation_latlon: `${newPosition.lat},${newPosition.lng}`,
              deliverylocation: formattedAddress,
            }));
          }

          if (map) {
            safeSetCenter(newPosition.lat, newPosition.lng);
          }
        } else {
          console.error(
            "Geocoder failed or no results found for dragged marker."
          );
        }
      });
    },
    [map]
  );

  useEffect(() => {
    if (
      locationValues?.pickuplocation_latlon?.length > 0 ||
      locationValues?.deliverylocation_latlon?.length > 0
    ) {
      setClear(true);
    }
  }, [
    locationValues?.pickuplocation_latlon,
    locationValues?.deliverylocation_latlon,
  ]);

  const closeModal = () => {
    setModalVisible(false);
  };

  const setLocation = () => {
    if (
      locationValues.pickuplocation_latlon?.length > 0 ||
      locationValues.deliverylocation_latlon?.length > 0
    ) {
      closeModal();
    } else {
      // alert('Please Select Location');
      toast.warning("Please select map locations", {
        toastrOptions,
      });
    }
  };

  const clearLocations = () => {
    setLocationValues((prevState) => ({
      ...prevState,
      pickuplocation_latlon: "",
      deliverylocation_latlon: "",
      pickuplocation: "",
      deliverylocation: "",
    }));
    // setMarkerPickup('');
    // setMarkerDelivery('')
    setClear(false);
    if (map) {
      map.setZoom(15);
      // map.panTo({ lat: 13.060644638899205, lng: 80.27414512422938 });
    }
    // closeModal();
  };

  let flatpickrInstance = null;
  const handleDateTimeClick = () => {
    if (!flatpickrInstance) {
      flatpickrInstance = flatpickr(pickUpTimeRef.current, {
        enableTime: true,
        dateFormat: "Y-m-d H:i:S",
        clickOpens: true,
        disableMobile: "true",
        onChange: function (selectedDates, dateStr) {
          setLocationValues((prev) => ({
            ...prev,
            pickuptime: dateStr,
          }));
        },
        onReady: function (selectedDates, dateStr, instance) {
          const buttonContainer = document.createElement("div");
          buttonContainer.classList.add("flatpickr-button-container");

          const okButton = document.createElement("button");
          okButton.textContent = "OK";
          okButton.classList.add("flatpickr-ok-button");

          const todayButton = document.createElement("button");
          todayButton.textContent = "Today";
          todayButton.classList.add("flatpickr-today-button");

          // Append buttons to the container and Flatpickr calendar
          buttonContainer.appendChild(todayButton);
          buttonContainer.appendChild(okButton);
          instance.calendarContainer.appendChild(buttonContainer);

          // Today button: Set to current date and trigger onChange
          todayButton.addEventListener("click", () => {
            instance.setDate(new Date(), true); // Pass `true` to trigger onChange
            instance.close();
          });

          // OK button: Close calendar if value exists
          okButton.addEventListener("click", () => {
            if (!pickUpTimeRef.current.value) {
              // alert("Please select a date and time.");
              toast.warning("Please select a date and time.", {
                toastrOptions,
              });
            } else {
              instance.close();
            }
          });
        },
      });
    }
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      // case "pickuplocation":
      // case "deliverylocation":
      case "pickuptime":
        if (!value) {
          error = "This field is required.";
        }
        break;
      case "pickuplocation_latlon":
      case "deliverylocation_latlon":
        if (!value) {
          error = "This field is required.";
        } else if (value === "null") {
          error = "";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const LocationSubmit = async (event) => {
    event.preventDefault();
    const newErrors = {};
    // Validate fields and collect errors
    Object.keys(locationValues).forEach((fieldName) => {
      const error = validateField(fieldName, locationValues[fieldName]);
      if (error) newErrors[fieldName] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      toast.warning("Please select map locations", { toastrOptions });
      // console.log(newErrors);
      return;
    }
    setErrors(newErrors);

    // Check if there are no errors
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true); // Show loading spinner
      // Handle payment type-specific logic
      let updatedValues = {
        ...locationValues,
      };

      const formData = new FormData();
      Object.keys(updatedValues).forEach((key) => {
        formData.append(key, updatedValues[key]);
      });
      formData.append("bookingid", routeParam);

      try {
        // Simulate delay for spinner demo
        await new Promise((resolve) => setTimeout(resolve, 2000));
        // Submit the form data via API
        const response = await fetch(HTTP_BASE_URL + "updateBookingdetails", {
          method: "post",
          headers: {
            "access-token": "508b1430-68f0-4b2f-9003-3c2a0eb2a54a",
            customertoken: customertoken,
          },
          body: formData,
        });
        const result = await response.json();
        // Handle API response
        if (result.code === 0) {
          setIsSubmitted(true);
          toast.success("Locations saved successfully!", { toastrOptions });
          setTimeout(function () {
            window.location.reload();
            setClosePopup(close);
          }, 1500);
        } else if (result.code === 1) {
          toast.error("Please complete all required fields correctly.", {
            toastrOptions,
          });
        } else {
          console.error("API error:", result);
        }
      } catch (error) {
        console.error("Submission failed:", error);
        // alert("An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false); // Hide loading spinner
      }
    }
  };

  if (loadError) {
    return <p className="pageloading">Error loading maps</p>;
  }
  if (!isLoaded) {
    return <p className="pageloading">Loading...</p>;
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {modalVisible && (
        <div className="mapModal">
          <div className="mapModal-content">
            <div className="mapModal-header">
              <button
                type="button"
                className="btn-close map-close"
                onClick={closeModal}
              >
                <AiOutlineCloseCircle />
              </button>
            </div>
            <div className="mapModal-body">
              {/* SEARCH BOX */}
              {activeMarker === "pickup" && (
                <Autocomplete
                  onLoad={(ref) => (pickupAutocompleteRef.current = ref)}
                  onPlaceChanged={() =>
                    handlePlaceSelect("pickup", pickupAutocompleteRef)
                  }
                >
                  <input
                    type="text"
                    placeholder="Search Pickup Location..."
                    style={{
                      width: "300px",
                      height: "40px",
                      padding: "5px",
                      margin: "5px",
                    }}
                  />
                </Autocomplete>
              )}

              {/* 🚚 Delivery Search Box */}
              {activeMarker === "delivery" && (
                <Autocomplete
                  onLoad={(ref) => (deliveryAutocompleteRef.current = ref)}
                  onPlaceChanged={() =>
                    handlePlaceSelect("delivery", deliveryAutocompleteRef)
                  }
                >
                  <input
                    type="text"
                    placeholder="Search Delivery Location..."
                    style={{
                      width: "300px",
                      height: "40px",
                      padding: "5px",
                      margin: "5px",
                    }}
                  />
                </Autocomplete>
              )}
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "400px",
                }}
                zoom={15}
                center={center}
                onClick={onMapClick}
                onLoad={(map) => setMap(map)}
              >
                {locationValues.pickuplocation_latlon && (
                  <Marker
                    // position={markerPickup}
                    position={{
                      lat: parseFloat(
                        locationValues.pickuplocation_latlon.split(",")[0]
                      ),
                      lng: parseFloat(
                        locationValues.pickuplocation_latlon.split(",")[1]
                      ),
                    }}
                    draggable={true}
                    // onClick={() => setActiveMarker('pickup')}
                    onDragEnd={(event) => onMarkerDragEnd(event, "pickup")}
                    label="P"
                  />
                )}
                {locationValues.deliverylocation_latlon && (
                  <Marker
                    // position={markerDelivery}
                    position={{
                      lat: parseFloat(
                        locationValues.deliverylocation_latlon.split(",")[0]
                      ),
                      lng: parseFloat(
                        locationValues.deliverylocation_latlon.split(",")[1]
                      ),
                    }}
                    draggable={true}
                    // onClick={() => setActiveMarker('delivery')}
                    onDragEnd={(event) => onMarkerDragEnd(event, "delivery")}
                    label="D"
                  />
                )}
              </GoogleMap>
            </div>
            <div className="mapModal-footer">
              {clear && (
                <button type="button" id="clear_btn" onClick={clearLocations}>
                  {" "}
                  Clear
                </button>
              )}
              <button
                type="button"
                className="btn map-submit btn-danger"
                onClick={setLocation}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="addGstModel">
        <div className="gstModal-content">
          <div className="gstModel-header">
            <p className="addGst">
              <FaRegSquarePlus /> Pickup & Delivery Schedule
            </p>
            <div className="btn-close" onClick={() => close(false)}>
              <AiOutlineCloseCircle />
            </div>
          </div>
          <div className="modal-body">
            <form onSubmit={LocationSubmit} autoComplete="off">
              <input
                type="hidden"
                name="pickuplocation"
                value={locationValues?.pickuplocation || ""}
              />
              <input
                type="hidden"
                name="deliverylocation"
                value={locationValues?.deliverylocation || ""}
              />
              <div className="form-col p-0">
                <label htmlFor="pickupLocationInput" className="form-label">
                  Pickup Map Location <span className="req-span">*</span>
                </label>
                <input
                  type="text"
                  name="pickuplocation_latlon"
                  id="pickupLocationInput"
                  placeholder="Select in Google map"
                  className="form-control map-input-img"
                  // value={locationValues?.pickuplocation_latlon || ''}
                  value={
                    locationValues?.pickuplocation_latlon === "null" ||
                    !locationValues?.pickuplocation_latlon
                      ? ""
                      : locationValues?.pickuplocation_latlon
                  }
                  readOnly
                  disabled={isSubmitting || isSubmitted}
                  onClick={() => {
                    setModalVisible(true);
                    setActiveMarker("pickup");
                    goToCurrentPickupLocation();
                  }}
                  onChange={handleOnChange}
                />
                {errors.pickuplocation_latlon && (
                  <p className="red text-para">
                    {errors.pickuplocation_latlon}
                  </p>
                )}
              </div>
              <div className="form-col p-0">
                <label htmlFor="deliveryLocationInput" className="form-label">
                  Delivery Map Location <span className="req-span">*</span>
                </label>
                <input
                  type="text"
                  name="deliverylocation_latlon"
                  id="deliveryLocationInput"
                  placeholder="Select in Google map"
                  className="form-control map-input-img"
                  // value={locationValues?.deliverylocation_latlon || ''}
                  value={
                    locationValues?.deliverylocation_latlon === "null" ||
                    !locationValues?.deliverylocation_latlon
                      ? ""
                      : locationValues?.deliverylocation_latlon
                  }
                  disabled={isSubmitting || isSubmitted}
                  readOnly
                  onClick={() => {
                    setModalVisible(true);
                    setActiveMarker("delivery");
                    goToCurrentDeliveryLocation();
                  }}
                  onChange={handleOnChange}
                />
                {errors.deliverylocation_latlon && (
                  <p className="red text-para">
                    {errors.deliverylocation_latlon}
                  </p>
                )}
              </div>

              <div className="form-col p-0 col">
                <label htmlFor="pickuptime" className="form-label">
                  Pickup Date & Time <span className="req-span">*</span>
                </label>
                <input
                  type="text"
                  id="pickuptime"
                  name="pickuptime"
                  disabled={isSubmitting || isSubmitted}
                  ref={pickUpTimeRef}
                  onClick={handleDateTimeClick}
                  value={locationValues?.pickuptime || ""}
                  onChange={handleOnChange}
                  className="form-control input-calendar-img"
                />
              </div>

              <div className="modal-footer pt-lg-2rm pt-4 text-center">
                <button
                  type="submit"
                  className="submit-btn location-submit"
                  disabled={isSubmitting || isSubmitted}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isSubmitted
                    ? "Submitted"
                    : "Submit"}
                  <span className="sub-loading">
                    {isSubmitting && (
                      <img
                        src={LoadingImage}
                        alt="Progress indicator"
                        width={25}
                        height={25}
                        loading="eager"
                      />
                    )}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingLocations;
